import { useActor } from '@xstate/react';
import { FC, useEffect, useState } from 'react';

import { Address } from 'api/api';

import { BlurCover } from '~src/blur/blur-overlay';
import { Portal } from '~src/blur/portal';
import { useAriaTitle } from '~src/commons/utils/hooks/a11y-hooks';
import { useTranslations } from '~src/commons/utils/translations/translate';
import { bootstrapConfig } from '~src/context/config';
import { useGlobalState } from '~src/global-state';
import { Heading } from '~src/ui/heading';
import { useIpc } from '~src/utils/ipc-utils';

import { AddressCardUI } from '~address-form/address-list/address-card';
import { AddressListFSM } from '~address-form/address-list/address-list-fsm';

import { SplitDeliveryAddressButton } from './components/split-address-button';
import { AddressFormBusinessProvider } from './context/use-address-form';
import { RecipientFormFlowProvider, useRecipientFormFlow } from './context/use-recipient-form-flow';
import { AddressFormModalProxy } from './form-templates/address-form-modal';
import { DeliveryAddressFormWithSuggestion } from './form-templates/delivery-address-form-with-suggestion';
import { ErrorModalFSM } from './form-templates/error-modal-fsm';
import { VerifyAddressModalProxy } from './form-templates/verify-address-modal';
import { isStreetNumberMissing } from './inputs/autocomplete/autocomplete-helpers';
import { isExternalAddressBook } from './utils/address-utils';
import { DELIVERY_ADDRESS_ARIA_TITLES, ModalStateNodes } from './utils/modal-utils';

export const DeliveryAddressRendererFSM: FC = () => {
  const globalState = useGlobalState();
  const { recipientFlowService, editInitialAddress, initialAddressEntry } = useRecipientFormFlow();
  const [state, send] = useActor(recipientFlowService);
  const [extendedAddress, setExtendedAddress] = useState<Address>();
  const ipc = useIpc();

  const { tICU } = useTranslations();

  useAriaTitle(tICU('widget.aria.CHECKOUT_ADDRESS_FORM.TITLE'), 'checkout');

  useEffect(() => {
    for (const title of Object.keys(DELIVERY_ADDRESS_ARIA_TITLES) as ModalStateNodes[]) {
      if (state.matches(title)) {
        const val = DELIVERY_ADDRESS_ARIA_TITLES[title];
        if (typeof val === 'string') {
          ipc.send({
            name: 'set_iframe_title',
            data: {
              title: tICU(val),
              target: 'fullscreen',
            },
          });
        } else {
          ipc.send({
            name: 'set_iframe_title',
            data: {
              title: tICU(
                val(
                  state.context,
                  globalState.address_form_configuration?.features?.frozen_fields_after_prefill
                    ?.fields
                )
              ),
              target: 'fullscreen',
            },
          });
        }
      }
    }

    return () => {
      ipc.send({
        name: 'unset_iframe_title',
        data: {
          target: 'fullscreen',
        },
      });
    };
  }, [state]);

  const optionalStreetNumber = globalState.address_form_configuration?.layout.fields.find(
    field => field.name === 'address_lines' && field.type === 'optional_address_line_street_number'
  );

  const getAddress = () => {
    if (isExternalAddressBook && !globalState.recipient) {
      return globalState.address_book?.addresses?.[0];
    } else {
      return editInitialAddress ? globalState.recipient : globalState.prefill_recipient;
    }
  };

  const getCheckoutStage = () => {
    switch (true) {
      case state.matches('checkout.addressSummary'):
        return isExternalAddressBook ? (
          <>
            <Heading>{tICU('widget.RECIPIENT_ADDRESS_HEADER')}</Heading>
            <SplitDeliveryAddressButton
              address={globalState.recipient || state.context.currentAddress! || {}}
              onTopBoxClick={() => send({ type: 'BUTTON_CLICK' })}
              onBottomBoxClick={() => send({ type: 'BUTTON_CLICK' })}
            />
          </>
        ) : (
          <AddressCardUI
            handleClick={() => send({ type: 'BUTTON_CLICK' })}
            address={globalState.recipient || state.context.currentAddress! || {}}
            boldenFirstLine
          />
        );
      case state.matches('checkout.inlineAddressList'):
        return (
          <>
            <Heading>{tICU('widget.EXTERNAL_ADDRESS_BOOK_HEADER')}</Heading>
            <AddressListFSM
              recipientState={{
                context: state.context,
                meta: {
                  fetching: state.hasTag('fetching'),
                },
              }}
              handleSubmit={address =>
                recipientFlowService.send({ type: 'SUBMIT', data: { address } })
              }
              handleAddNewAddress={address =>
                recipientFlowService.send({ type: 'ADD_NEW_ADDRESS', data: { address } })
              }
              handleOpenAddress={address =>
                recipientFlowService.send({ type: 'OPEN_ADDRESS', data: { address } })
              }
              buttonMessage={tICU('widget.delivery_address.CONTINUE_BTN')}
            />
          </>
        );
      case state.matches('checkout.inlineAddressForm'):
      default:
        return (
          <>
            {isExternalAddressBook && <Heading>{tICU('widget.RECIPIENT_ADDRESS_HEADER')}</Heading>}
            <AddressFormBusinessProvider
              onSubmit={(address, onError) => {
                send({ type: 'SUBMIT', data: { address, onError } });
              }}
              address={getAddress()}
              addressVerified={editInitialAddress}
              isModalForm={false}
              extendedAddress={extendedAddress}
              setExtendedAddress={setExtendedAddress}
            >
              <DeliveryAddressFormWithSuggestion
                addressFlowService={recipientFlowService}
                triggerExtendedValidation={data =>
                  send({
                    type: 'SHOW_EXTENDED_VALIDATION_MODAL',
                    data,
                  })
                }
              />
            </AddressFormBusinessProvider>
          </>
        );
    }
  };

  const getModalStage = () => {
    switch (true) {
      case state.matches('modal.modalAddressList'):
      case state.matches('modal.modalAddressForm'):
        return (
          <AddressFormModalProxy
            step={state.matches('modal.modalAddressList') ? 'list' : 'form'}
            triggerExtendedValidation={data =>
              send({
                type: 'SHOW_EXTENDED_VALIDATION_MODAL',
                data,
              })
            }
            handleClose={() => recipientFlowService.send({ type: 'CLOSE' })}
            handleSubmit={address =>
              recipientFlowService.send({ type: 'SUBMIT', data: { address } })
            }
            handleDelete={address =>
              recipientFlowService.send({ type: 'DELETE', data: { address } })
            }
            handleAddNewAddress={address =>
              recipientFlowService.send({ type: 'ADD_NEW_ADDRESS', data: { address } })
            }
            handleOpenAddress={address =>
              recipientFlowService.send({ type: 'OPEN_ADDRESS', data: { address } })
            }
            extendedAddress={extendedAddress}
            setExtendedAddress={setExtendedAddress}
          />
        );
      case state.matches('modal.extendedValidationModal'):
        return (
          <VerifyAddressModalProxy
            enteredAddress={state.context.extendedValidation.address}
            suggestion={state.context.extendedValidation.suggestion}
            isSubmissionBlocking={state.context.extendedValidation.isSubmissionBlocking}
            handleClose={() => send({ type: 'CLOSE', data: { shouldResetForm: true } })}
            handlePrimaryButton={address => {
              if (optionalStreetNumber && isStreetNumberMissing(address.address_lines?.[0] || '')) {
                send({ type: 'CLOSE', data: { shouldResetForm: true } });
                setExtendedAddress(state.context.extendedValidation.suggestion?.address);
              } else {
                send({ type: 'SUBMIT_ADDRESS', data: { address } });
              }
            }}
            handleSecondaryButton={address =>
              state.context.extendedValidation.isSubmissionBlocking
                ? send({ type: 'CLOSE', data: { shouldResetForm: true } })
                : send({ type: 'SUBMIT_ADDRESS', data: { address } })
            }
          />
        );
      case state.matches('modal.errorModal'):
        return <ErrorModalFSM onClose={() => recipientFlowService.send({ type: 'CLOSE' })} />;

      default:
        return null;
    }
  };

  return (
    <>
      {getCheckoutStage()}
      {getModalStage()}
      <Portal
        showCondition={
          !!bootstrapConfig.externalAddressBookProvider
            ? !globalState.recipient
            : (initialAddressEntry || editInitialAddress) &&
              !state.matches('checkout.addressSummary')
        }
        deps={
          !!bootstrapConfig.externalAddressBookProvider
            ? [globalState.recipient]
            : [initialAddressEntry, editInitialAddress, state.matches('checkout.addressSummary')]
        }
      >
        <BlurCover />
      </Portal>
    </>
  );
};

export const DeliveryAddressManagerFSM: React.FC = () => {
  return (
    <RecipientFormFlowProvider>
      <DeliveryAddressRendererFSM />
    </RecipientFormFlowProvider>
  );
};
